// Function index:
//   renderParties(host, ctx) — fetch + render the parties list. Each row has
//     a "prefs ▾" toggle that expands the inline prefs editor (parties/prefs-editor.ts)
//     and an "end combat" escape hatch (two-click arm, no browser dialog) for
//     force-ending a wedged fight.
//
// Pure-DOM façade. The 150-line prefs editor lives in parties/prefs-editor.ts.

import { endPartyCombat, getPartyPrefs, listAdminParties, type AdminParty } from '../../api.js';
import { esc, type AdminPanelCtx } from './helpers.js';
import { prefsEditor } from './parties/prefs-editor.js';

export async function renderParties(host: HTMLElement, ctx: AdminPanelCtx): Promise<void> {
  host.innerHTML = 'Loading…';
  try {
    const { parties } = await listAdminParties();
    host.innerHTML = '';
    if (parties.length === 0) {
      const meta = document.createElement('div');
      meta.className = 'party-meta';
      meta.textContent = 'No parties yet.';
      host.appendChild(meta);
      return;
    }
    for (const p of parties) host.appendChild(partyRow(p, host, ctx));
  } catch (err) {
    host.innerHTML = '';
    ctx.setError(`parties: ${(err as Error).message}`);
  }
}

function partyRow(p: AdminParty, host: HTMLElement, ctx: AdminPanelCtx): HTMLDivElement {
  // Wrapper so the inline prefs editor can attach beneath the row.
  const wrap = document.createElement('div');
  wrap.className = 'party-list';
  wrap.style.flexDirection = 'column';
  wrap.style.gap = '0';

  const row = document.createElement('div');
  row.className = 'party-row';
  row.style.cursor = 'default';
  const last = p.last_activity_at ? new Date(p.last_activity_at).toISOString().slice(0, 16).replace('T', ' ') : '—';
  row.innerHTML = `
    <div>
      <div>${esc(p.name)} <span class="party-meta">· ${esc(p.world_name)} (${esc(p.world_setting)})</span></div>
      <div class="party-meta">id ${p.id} · ${p.member_count} member${p.member_count === 1 ? '' : 's'} · last activity ${last}</div>
    </div>
    <div style="display:flex; align-items:center; gap:8px;">
      <span class="party-meta">invite ${esc(p.invite_code)}</span>
      <button class="secondary" data-action="end-combat" data-id="${p.id}">end combat</button>
      <button class="secondary" data-action="prefs" data-id="${p.id}">prefs ▾</button>
    </div>
  `;
  wrap.appendChild(row);

  // Escape hatch for a wedged fight. Two-click arm instead of a confirm()
  // dialog; the server answers 404 when there is nothing to end.
  const endBtn = row.querySelector<HTMLButtonElement>('button[data-action="end-combat"]')!;
  let armed = false;
  endBtn.addEventListener('click', async () => {
    if (!armed) {
      armed = true;
      endBtn.textContent = 'really end?';
      setTimeout(() => { armed = false; endBtn.textContent = 'end combat'; }, 4000);
      return;
    }
    armed = false;
    endBtn.disabled = true;
    try {
      await endPartyCombat(p.id);
      endBtn.textContent = 'combat ended ✓';
    } catch (err) {
      const msg = (err as Error).message;
      if (/no active combat/i.test(msg)) {
        endBtn.textContent = 'no combat';
        setTimeout(() => { endBtn.textContent = 'end combat'; endBtn.disabled = false; }, 2500);
      } else {
        ctx.setError(`end combat: ${msg}`);
        endBtn.textContent = 'end combat';
        endBtn.disabled = false;
      }
    }
  });

  const editorHost = document.createElement('div');
  editorHost.hidden = true;
  editorHost.style.background = '#101010';
  editorHost.style.border = '1px solid #2a2a2a';
  editorHost.style.borderTop = 'none';
  editorHost.style.padding = '12px 14px';
  editorHost.style.borderRadius = '0 0 3px 3px';
  editorHost.style.marginBottom = '4px';
  wrap.appendChild(editorHost);

  const btn = row.querySelector<HTMLButtonElement>('button[data-action="prefs"]')!;
  btn.addEventListener('click', async () => {
    if (!editorHost.hidden) { editorHost.hidden = true; btn.textContent = 'prefs ▾'; return; }
    btn.disabled = true;
    try {
      const { prefs } = await getPartyPrefs(p.id);
      editorHost.innerHTML = '';
      editorHost.appendChild(prefsEditor(p.id, prefs, host, ctx, () => {
        editorHost.hidden = true;
        btn.textContent = 'prefs ▾';
      }));
      editorHost.hidden = false;
      btn.textContent = 'prefs ▴';
    } catch (err) {
      ctx.setError(`prefs: ${(err as Error).message}`);
    } finally {
      btn.disabled = false;
    }
  });

  return wrap;
}
